/**
 * Shared helper for authenticated practice API calls. Fetches an Auth0 ID token
 * with a proactive expiry check (a cached token that is expired or about to be
 * is refreshed before the request goes out), then runs `fn(token)`.
 *
 * Reactive 401 recovery — force-refresh + replay once, or redirect to login when
 * the session is unrecoverable — is handled globally for EVERY authenticated
 * request by the interceptor in `fetchWithConfig` (src/services/apiUtils.ts,
 * backed by src/services/authBridge.ts). It is intentionally NOT duplicated
 * here: every consumer of this hook issues its request through `fetchWithConfig`,
 * so the interceptor already owns the 401 path.
 *
 * Consumed by usePracticeSession, useDailySessionPreview, useTopicOverview and
 * the other practice/analytics/teacher hooks.
 */
import { useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

import { fetchFreshIdToken } from '@/auth/freshIdToken';

export interface AuthenticatedCall {
  /** Runs `fn(token)` with a proactively-refreshed Auth0 ID token. */
  callWithAuth: <T>(fn: (token: string) => Promise<T>) => Promise<T>;
}

export function useAuthenticatedCall(): AuthenticatedCall {
  const { getIdTokenClaims, getAccessTokenSilently } = useAuth0();

  const callWithAuth = useCallback(
    async <T>(fn: (token: string) => Promise<T>): Promise<T> => {
      const token = await fetchFreshIdToken({
        getIdTokenClaims,
        getAccessTokenSilently,
      });
      if (!token) throw new Error('Not authenticated');
      return fn(token);
    },
    [getIdTokenClaims, getAccessTokenSilently]
  );

  return { callWithAuth };
}
